import { useEffect, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { errorMessage, post } from '../lib/api';
import { Alert, Button, Card, Field, GarlicLogo, Input } from '../ui/primitives';
import type { DevSession } from './DevApp';

export function DevLogin({ session }: { session?: DevSession }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'password' | 'mfa_setup' | 'mfa_verify'>(session?.next ?? 'password');
  const refresh = () => qc.invalidateQueries({ queryKey: ['dev-session'] });

  return (
    <main className="grid min-h-dvh place-items-center bg-paper-2 px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <GarlicLogo suffix="dev" />
        </div>
        <Card className="p-6">
          {step === 'password' && <PasswordStep onNext={setStep} />}
          {step === 'mfa_setup' && <MfaSetupStep onDone={refresh} />}
          {step === 'mfa_verify' && <MfaVerifyStep onDone={refresh} />}
        </Card>
        <p className="mt-4 text-center text-xs text-ink-3">Área restrita ao proprietário da plataforma. Todos os acessos são registrados.</p>
      </div>
    </main>
  );
}

function PasswordStep({ onNext }: { onNext: (s: 'mfa_setup' | 'mfa_verify') => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const r = await post<{ next: 'mfa_setup' | 'mfa_verify' }>('/api/dev/auth/login', { email, password });
      onNext(r.next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Painel do desenvolvedor</h1>
        <p className="text-sm text-ink-3">Entre com seu e-mail e senha.</p>
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label="E-mail">{(id) => <Input id={id} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
      <Field label="Senha">
        {(id) => <Input id={id} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}
      </Field>
      <Button type="submit" className="w-full" loading={busy} icon={<KeyRound className="size-4" />}>
        Continuar
      </Button>
    </form>
  );
}

function CodeForm({ onDone, label }: { onDone: () => void; label: string }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await post('/api/dev/auth/mfa/verify', { code });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      {error && <Alert tone="danger">{error}</Alert>}
      <Field label={label}>
        {(id) => (
          <Input
            id={id}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            required
            autoFocus
            className="text-center font-mono text-xl tracking-[0.4em]"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
        )}
      </Field>
      <Button type="submit" className="w-full" loading={busy} disabled={code.length !== 6} icon={<ShieldCheck className="size-4" />}>
        Verificar
      </Button>
    </form>
  );
}

function MfaVerifyStep({ onDone }: { onDone: () => void }) {
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Verificação em duas etapas</h1>
        <p className="text-sm text-ink-3">Digite o código de 6 dígitos do seu app autenticador.</p>
      </div>
      <CodeForm onDone={onDone} label="Código" />
    </div>
  );
}

function MfaSetupStep({ onDone }: { onDone: () => void }) {
  const [data, setData] = useState<{ qr: string; secret: string }>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    let alive = true;
    post<{ otpauthUri: string; secret: string }>('/api/dev/auth/mfa/setup')
      .then(async (r) => {
        const qr = await QRCode.toDataURL(r.otpauthUri, { margin: 1, width: 220, color: { dark: '#1f1a17', light: '#ffffff' } });
        if (alive) setData({ qr, secret: r.secret });
      })
      .catch((e) => alive && setError(errorMessage(e)));
    return () => {
      alive = false;
    };
  }, []);
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Ative a verificação em duas etapas</h1>
        <p className="text-sm text-ink-3">
          Obrigatória para o painel DEV. Escaneie com Google Authenticator, Microsoft Authenticator, 1Password ou similar.
        </p>
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      {data && (
        <>
          <img src={data.qr} alt="QR Code para o app autenticador" className="mx-auto size-48 rounded-xl border border-line" />
          <details className="text-center text-xs text-ink-3">
            <summary className="cursor-pointer">Não consegue escanear? Digite a chave</summary>
            <code className="mt-2 block break-all rounded-lg bg-paper-2 p-2 font-mono text-ink">{data.secret}</code>
          </details>
          <CodeForm onDone={onDone} label="Código gerado pelo app" />
        </>
      )}
    </div>
  );
}
